/**
 * The client portal's Safety register (MB's R220; decisions 1023, 1035, 1037; docs/specs/safety-register.md §10;
 * 20261104238000_client_safety_register.sql): every record of the client organisation's projects that the operator's
 * tenant admins and managers approved for the client, the whole record (decision 1035), read only through
 * `my_safety_register_all` (never the tables). Preview as client reads the same answer for the organisation previewed.
 * The linked photos' display size and thumbnail are read under the storage policy for approved entries.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  clientRegisterRecords,
  photoObjectPath,
  type ClientRegisterRecord,
  type ClientRegisterRow,
  type PhotoRendition,
} from '@teslyn/domain';
import { missingView, plainPortalError } from '../portal/api.ts';
import { signedPhotoUrls } from '../portal/photosApi.ts';

/** The organisation's approved records, newest first; none while Teslyn Cloud has not got 20261104238000. */
export async function loadClientSafetyRegister(
  sb: SupabaseClient,
  clientOrgId: string,
): Promise<ClientRegisterRecord[]> {
  const { data, error } = (await sb.rpc('my_safety_register_all', { p_client_org: clientOrgId })) as {
    data: unknown;
    error: { code?: string; message: string } | null;
  };
  if (missingView(error) || /my_safety_register_all/.test(error?.message ?? '')) return [];
  if (error) throw new Error(plainPortalError(error));
  return clientRegisterRecords((Array.isArray(data) ? data : []) as ClientRegisterRow[]);
}

/** The object of a linked photo's display size or thumbnail (never the original). */
export function registerPhotoPath(
  tenantId: string,
  projectId: string,
  photoId: string,
  rendition: Exclude<PhotoRendition, 'original'>,
): string | null {
  try {
    return photoObjectPath(tenantId, projectId, photoId, rendition);
  } catch {
    return null;
  }
}

/** 5-minute addresses of the thumbnails, by photo id; those the policy refuses (or not uploaded yet) left out. */
export async function registerThumbUrls(
  sb: SupabaseClient,
  tenantId: string,
  projectId: string,
  photoIds: readonly string[],
): Promise<Map<string, string>> {
  const byPath = new Map<string, string>();
  for (const id of photoIds) {
    const p = registerPhotoPath(tenantId, projectId, id, 'thumb');
    if (p) byPath.set(p, id);
  }
  const signed = await signedPhotoUrls(sb, [...byPath.keys()]).catch(() => new Map<string, string>());
  const out = new Map<string, string>();
  for (const [path, url] of signed) {
    const id = byPath.get(path);
    if (id) out.set(id, url);
  }
  return out;
}
